import { Fragment, useCallback, useEffect, useMemo, useState } from "react";
import { brandApi } from "../lib/brand-api";
import { Button } from "../components/ui/Button";
import { useToast } from "../components/ui/Toast";

// PR #65 — Brand-scoped activity feed. Reads /api/brand/:slug/activity
// (session-cookie auth), the full keyset-paginated tenant_change_log. Distinct
// from the admin Activity.tsx, which hits /api/audit (brand_retailer_link audit).

interface ActivityRow {
  id: string;
  tenant_id: string;
  tenant_store_id: string | null;
  upload_run_id: string | null;
  source: string;
  change_type: string;
  changed_at: string;
  changed_by: string | null;
  source_external_id: string | null;
  display_name_at_event: string | null;
  previous_identity: Record<string, unknown> | null;
  new_identity: Record<string, unknown> | null;
  changes: Array<{ field?: string; old?: unknown; new?: unknown }> | null;
  is_suggestion: boolean;
  suggestion_state: string | null;
  notes: string | null;
  store_display_name: string | null;
  store_status: string | null;
}

const SOURCES: { value: string; label: string }[] = [
  { value: "all", label: "All sources" },
  { value: "cm", label: "Customer Master" },
  { value: "wtb", label: "Where-to-Buy" },
  { value: "gbp", label: "Google Business" },
  { value: "manual", label: "Manual" },
  { value: "system", label: "System" },
];

const CHANGE_TYPES: { key: string; label: string }[] = [
  { key: "created", label: "created" },
  { key: "renamed", label: "renamed" },
  { key: "removed", label: "removed" },
  { key: "reappeared", label: "reappeared" },
  { key: "archived", label: "archived" },
  { key: "unarchived", label: "unarchived" },
  { key: "merged", label: "merged" },
  { key: "chain_changed", label: "chain changed" },
  { key: "address_changed", label: "address changed" },
];

const CHANGE_TYPE_COLORS: Record<string, string> = {
  created: "bg-green-100 text-green-800 border-green-200",
  reappeared: "bg-emerald-100 text-emerald-800 border-emerald-200",
  unarchived: "bg-emerald-100 text-emerald-800 border-emerald-200",
  renamed: "bg-amber-100 text-amber-800 border-amber-200",
  removed: "bg-red-100 text-red-800 border-red-200",
  archived: "bg-gray-200 text-gray-700 border-gray-300",
  merged: "bg-purple-100 text-purple-800 border-purple-200",
  chain_changed: "bg-indigo-100 text-indigo-800 border-indigo-200",
  address_changed: "bg-blue-100 text-blue-800 border-blue-200",
};

const SOURCE_BADGE: Record<string, string> = {
  wtb: "bg-sky-100 text-sky-800 border-sky-200",
  cm: "bg-violet-100 text-violet-800 border-violet-200",
  gbp: "bg-orange-100 text-orange-800 border-orange-200",
  manual: "bg-gray-100 text-gray-700 border-gray-200",
  system: "bg-gray-100 text-gray-700 border-gray-200",
};

function changeTypeColor(t: string): string {
  return CHANGE_TYPE_COLORS[t] || "bg-gray-100 text-gray-700 border-gray-200";
}

function formatWhen(iso: string): string {
  const d = new Date(iso);
  const t = new Intl.DateTimeFormat(undefined, { hour: "2-digit", minute: "2-digit", hour12: false }).format(d);
  const day = new Intl.DateTimeFormat(undefined, { month: "short", day: "numeric" }).format(d);
  return `${t} · ${day}`;
}

function changeSnippet(r: ActivityRow): string | null {
  if (r.changes && r.changes.length > 0) {
    const c = r.changes[0];
    if (c.field === "display_name") return `was: ${String(c.old ?? "—")}`;
    if (c.field) return `${c.field}: ${String(c.old ?? "—")} → ${String(c.new ?? "—")}`;
  }
  const prev = r.previous_identity as { display_name?: string } | null;
  if (prev?.display_name) return `was: ${prev.display_name}`;
  if (r.notes) return r.notes;
  return null;
}

const PAGE_SIZE = 50;

export default function BrandActivity({ slug }: { slug: string }) {
  const toast = useToast();
  const base = `/api/brand/${encodeURIComponent(slug)}/activity`;

  const [source, setSource] = useState<string>("all");
  const [types, setTypes] = useState<Set<string>>(new Set());
  const [rows, setRows] = useState<ActivityRow[]>([]);
  const [nextCursor, setNextCursor] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  const [loadingMore, setLoadingMore] = useState(false);
  const [expanded, setExpanded] = useState<Set<string>>(new Set());

  const params = useMemo(() => {
    const p = new URLSearchParams();
    p.set("limit", String(PAGE_SIZE));
    if (source !== "all") p.set("source", source);
    if (types.size) p.set("change_type", [...types].join(","));
    return p;
  }, [source, types]);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await brandApi.get(`${base}?${params.toString()}`);
      setRows(r.rows || []);
      setNextCursor(r.next_cursor || null);
    } catch (err) {
      toast.push(`Failed to load activity: ${(err as Error).message}`, "error");
    } finally {
      setLoading(false);
    }
  }, [base, params, toast]);

  useEffect(() => {
    load();
  }, [load]);

  const loadMore = async () => {
    if (!nextCursor) return;
    setLoadingMore(true);
    try {
      const p = new URLSearchParams(params);
      p.set("cursor", nextCursor);
      const r = await brandApi.get(`${base}?${p.toString()}`);
      setRows((prev) => [...prev, ...(r.rows || [])]);
      setNextCursor(r.next_cursor || null);
    } catch (err) {
      toast.push(`Failed to load more: ${(err as Error).message}`, "error");
    } finally {
      setLoadingMore(false);
    }
  };

  const toggleType = (k: string) =>
    setTypes((prev) => {
      const next = new Set(prev);
      if (next.has(k)) next.delete(k);
      else next.add(k);
      return next;
    });

  const toggleExpanded = (id: string) =>
    setExpanded((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });

  return (
    <div className="space-y-4">
      <div>
        <a href={`#/brand/${encodeURIComponent(slug)}/my-chains`} className="text-xs text-gray-500 hover:text-gray-800">
          ← My Chains
        </a>
        <h2 className="mt-1 text-2xl font-semibold text-gray-900">Activity</h2>
        <p className="text-sm text-gray-600 mt-1">
          Every chain, store, and membership change recorded for your account.
        </p>
      </div>

      <div className="sticky top-[52px] z-10 rounded-lg border border-gray-200 bg-white/95 backdrop-blur p-4 shadow-sm space-y-3">
        <div className="flex flex-wrap items-center gap-3">
          <select
            value={source}
            onChange={(e) => setSource(e.target.value)}
            className="text-sm border border-gray-200 rounded-md px-2 py-1"
          >
            {SOURCES.map((s) => (
              <option key={s.value} value={s.value}>
                {s.label}
              </option>
            ))}
          </select>
          {types.size > 0 && (
            <button type="button" className="text-xs text-gray-500 hover:text-gray-800" onClick={() => setTypes(new Set())}>
              Clear types
            </button>
          )}
        </div>
        <div className="flex flex-wrap gap-1.5">
          {CHANGE_TYPES.map((o) => {
            const selected = types.has(o.key);
            return (
              <button
                key={o.key}
                type="button"
                onClick={() => toggleType(o.key)}
                className={`text-xs rounded-full border px-2.5 py-1 transition-colors ${
                  selected
                    ? "bg-accent-500 text-white border-accent-500"
                    : "bg-white text-gray-700 border-gray-200 hover:bg-gray-50"
                }`}
              >
                {o.label}
              </button>
            );
          })}
        </div>
      </div>

      <div className="rounded-lg border border-gray-200 bg-white shadow-sm overflow-hidden">
        {loading && rows.length === 0 ? (
          <div className="p-10 text-center text-sm text-gray-500">Loading…</div>
        ) : rows.length === 0 ? (
          <div className="p-10 text-center">
            <div className="mx-auto h-10 w-10 rounded-full bg-gray-100 flex items-center justify-center text-gray-400 mb-2">○</div>
            <div className="text-sm font-medium text-gray-800">No activity yet.</div>
            <div className="text-xs text-gray-500 mt-1">Chain edits and uploads will appear here.</div>
          </div>
        ) : (
          <table className="w-full text-sm">
            <thead className="bg-gray-50 text-xs uppercase tracking-wide text-gray-500">
              <tr>
                <th className="text-left px-4 py-2 font-medium">When</th>
                <th className="text-left px-4 py-2 font-medium">Store</th>
                <th className="text-left px-4 py-2 font-medium">Change</th>
                <th className="text-left px-4 py-2 font-medium">Detail</th>
                <th className="text-left px-4 py-2 font-medium">Source</th>
                <th className="text-left px-4 py-2 font-medium w-8"></th>
              </tr>
            </thead>
            <tbody>
              {rows.map((r) => {
                const isOpen = expanded.has(r.id);
                const name = r.store_display_name || r.display_name_at_event || "—";
                return (
                  <Fragment key={r.id}>
                    <tr className="border-t border-gray-100 hover:bg-gray-50 align-top">
                      <td className="px-4 py-2 whitespace-nowrap" title={r.changed_at}>
                        {formatWhen(r.changed_at)}
                      </td>
                      <td className="px-4 py-2">
                        <div className="font-medium text-gray-900">{name}</div>
                        {r.source_external_id && (
                          <div className="text-xs text-gray-400 font-mono">{r.source_external_id}</div>
                        )}
                        {r.store_status && r.store_status !== "active" && (
                          <span className="inline-flex items-center rounded-full bg-gray-100 text-gray-500 border border-gray-200 px-1.5 py-0.5 text-[10px] mt-0.5">
                            {r.store_status}
                          </span>
                        )}
                      </td>
                      <td className="px-4 py-2">
                        <span className={`inline-flex items-center rounded-full border px-2 py-0.5 text-xs ${changeTypeColor(r.change_type)}`}>
                          {r.change_type}
                        </span>
                        {r.is_suggestion && (
                          <span className="ml-1 inline-flex items-center rounded-full border border-amber-200 bg-amber-50 text-amber-700 px-1.5 py-0.5 text-[10px]">
                            suggestion
                          </span>
                        )}
                      </td>
                      <td className="px-4 py-2 text-xs text-gray-600">{changeSnippet(r) || "—"}</td>
                      <td className="px-4 py-2">
                        <span className={`inline-flex items-center rounded-full border px-2 py-0.5 text-[10px] uppercase ${SOURCE_BADGE[r.source] || SOURCE_BADGE.system}`}>
                          {r.source}
                        </span>
                      </td>
                      <td className="px-4 py-2">
                        <button
                          className="text-gray-400 hover:text-gray-700"
                          onClick={() => toggleExpanded(r.id)}
                          aria-label={isOpen ? "Collapse" : "Expand"}
                        >
                          {isOpen ? "▾" : "▸"}
                        </button>
                      </td>
                    </tr>
                    {isOpen && (
                      <tr className="bg-gray-50 border-t border-gray-100">
                        <td colSpan={6} className="px-4 py-3">
                          {r.notes && <div className="text-xs text-gray-600 mb-2">{r.notes}</div>}
                          <pre className="text-xs text-gray-700 whitespace-pre-wrap break-all">
{JSON.stringify({ previous_identity: r.previous_identity, new_identity: r.new_identity, changes: r.changes }, null, 2)}
                          </pre>
                        </td>
                      </tr>
                    )}
                  </Fragment>
                );
              })}
            </tbody>
          </table>
        )}
        <div className="flex items-center justify-between px-4 py-3 border-t border-gray-100 text-xs text-gray-500">
          <div>{rows.length > 0 ? `${rows.length} loaded` : "0 results"}</div>
          <Button variant="ghost" onClick={loadMore} disabled={!nextCursor || loadingMore || loading}>
            {loadingMore ? "Loading…" : "Load more"}
          </Button>
        </div>
      </div>
    </div>
  );
}
